import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, ExternalLink, Loader2, PlugZap, RefreshCw, RotateCcw, Sheet, UploadCloud } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPORT_TABS = [
  { key: 'crm', label: 'CRM Database' },
  { key: 'newsletter_email', label: 'Newsletter Email' },
  { key: 'newsletter_whatsapp', label: 'Newsletter WhatsApp' },
  { key: 'calendario_eventi', label: 'Calendario Eventi' },
];

const APPS_SCRIPT_CODE = `function doPost(e) {
  var TOKEN = 'IL_TUO_TOKEN'; // stesso valore di CRM_SHEETS_WEBHOOK_TOKEN
  var data = JSON.parse(e.postData.contents);
  if (data.token !== TOKEN) return out({ ok: false, error: 'unauthorized' });
  if (data.action === 'ping') return out({ ok: true, action: 'pong' });
  var ss = SpreadsheetApp.getActiveSpreadsheet();
  var sh = ss.getSheetByName(data.tab) || ss.insertSheet(data.tab);
  if (sh.getLastRow() === 0) sh.appendRow(data.headers);
  var keyIdx = data.headers.indexOf(data.key_field) + 1;
  var last = sh.getLastRow();
  var rowNum = 0;
  if (last > 1 && keyIdx > 0) {
    var vals = sh.getRange(2, keyIdx, last - 1, 1).getValues();
    for (var i = 0; i < vals.length; i++) {
      if (String(vals[i][0]).trim().toLowerCase() === String(data.key_value).trim().toLowerCase()) { rowNum = i + 2; break; }
    }
  }
  if (rowNum) { sh.getRange(rowNum, 1, 1, data.row.length).setValues([data.row]); return out({ ok: true, action: 'updated' }); }
  sh.appendRow(data.row);
  return out({ ok: true, action: 'appended' });
}
function out(o) { return ContentService.createTextOutput(JSON.stringify(o)).setMimeType(ContentService.MimeType.JSON); }`;

export const CrmStatusBanner = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [testing, setTesting] = useState(false);
  const [exporting, setExporting] = useState('');
  const [whUrl, setWhUrl] = useState('');
  const [whToken, setWhToken] = useState('');
  const [savingWh, setSavingWh] = useState(false);
  const headers = { 'X-Admin-Token': token };

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/crm/status`, { headers });
      setStatus(r.data);
    } catch {
      /* banner is informative only */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const pushAll = async () => {
    setPushing(true);
    try {
      const r = await axios.post(`${API}/admin/crm/sheet-sync-all`, {}, { headers });
      if (r.data.pending_credentials) {
        toast.info(`Provider non ancora configurato: ${r.data.queued} record accodati in outbox (verranno spinti al primo collegamento)`);
      } else {
        toast.success(`Sheet CRM: ${r.data.synced} sincronizzati${r.data.failed ? `, ${r.data.failed} falliti` : ''}${r.data.queued ? `, ${r.data.queued} in coda` : ''}`);
      }
      await fetchStatus();
    } catch {
      toast.error('Sync verso il Google Sheet fallita');
    } finally {
      setPushing(false);
    }
  };

  const retryOutbox = async () => {
    setRetrying(true);
    try {
      const r = await axios.post(`${API}/admin/crm/sheet-retry-pending`, { only_failed: false }, { headers });
      toast.success(`Retry outbox: ${r.data.synced} sincronizzati, ${r.data.failed} falliti (${r.data.attempted} tentati)`);
      await fetchStatus();
    } catch (err) {
      if (err?.response?.status === 409) toast.error('Nessun provider configurato: imposta webhook o service account');
      else toast.error('Retry fallito');
    } finally {
      setRetrying(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/sheet-sync/test`, {}, { headers });
      if (r.data.ok) toast.success(`Collegamento OK · ${r.data.provider} · ${r.data.detail}`);
      else if (!r.data.provider) toast.info('Nessun provider configurato: la sync è pronta, manca il webhook (o il JSON)');
      else toast.error(`Test fallito · ${r.data.provider} · ${r.data.detail}`);
    } catch {
      toast.error('Test collegamento fallito');
    } finally {
      setTesting(false);
    }
  };

  const saveWebhook = async () => {
    if (!whUrl.trim()) { toast.error('Incolla l\'URL /exec del webhook Apps Script'); return; }
    setSavingWh(true);
    try {
      const r = await axios.post(`${API}/admin/crm/webhook-config`, { url: whUrl.trim(), token: whToken.trim() }, { headers });
      if (r.data.test?.ok) toast.success('Webhook collegato e testato con successo');
      else toast.info('Webhook salvato: test collegamento non riuscito, verifica URL/token');
      setWhUrl(''); setWhToken('');
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Salvataggio webhook fallito');
    } finally {
      setSavingWh(false);
    }
  };

  const exportTab = async (tabKey) => {
    setExporting(tabKey);
    try {
      const r = await axios.get(`${API}/admin/crm/export-csv?tab=${tabKey}`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `prime-vision-${tabKey}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('CSV esportato');
    } catch {
      toast.error('Export CSV fallito');
    } finally {
      setExporting('');
    }
  };

  if (!status) return null;
  const provider = status.provider;
  const ok = status.configured;
  const outbox = status.outbox || { pending: 0, failed: 0, synced: 0 };

  return (
    <div
      data-testid="pv-admin-crm-banner"
      className={`mb-6 relative rounded-2xl border p-5 md:p-6 ${
        ok ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200' : 'bg-gradient-to-br from-amber-50 to-white border-amber-300'
      }`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className={`shrink-0 w-11 h-11 rounded-xl bg-white border flex items-center justify-center ${ok ? 'border-emerald-300' : 'border-amber-300'}`}>
            <Sheet className={`w-5 h-5 ${ok ? 'text-emerald-600' : 'text-amber-600'}`} />
          </div>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">
              Google Sheet Sync · CRM principale
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                data-testid="pv-admin-crm-sheet-status"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] border ${
                  ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-100 border-amber-300 text-amber-800'
                }`}
              >
                {provider === 'service_account'
                  ? '● Collegato · Service Account'
                  : provider === 'apps_script'
                    ? '● Collegato · Apps Script Webhook'
                    : '● Sync pronto · webhook mancante'}
              </span>
              <a
                href={status.sheet_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pv-admin-crm-sheet-link"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-700 text-[11px] font-bold hover:border-emerald-300 hover:text-emerald-700 transition-colors"
              >
                <ExternalLink className="w-3 h-3" /> Apri il CRM
              </a>
            </div>
            <p className="text-[12.5px] text-slate-600 mt-2 leading-snug max-w-2xl">
              {ok
                ? 'Ogni lead (webinar, brochure, FSE, call, form contatto) viene sincronizzato in automatico sui 4 tab del Google Sheet: CRM Database, Newsletter Email, Newsletter WhatsApp, Calendario Eventi.'
                : 'Nessun dato viene perso: ogni lead è salvato nel CRM interno e accodato nella outbox. Appena configuri il webhook Apps Script (istruzioni qui sotto) o il JSON del service account, tutto viene spinto sul foglio con un click.'}
            </p>
            {status.last_error && outbox.failed > 0 && (
              <p className="text-[11.5px] text-red-600 font-semibold mt-1.5" data-testid="pv-admin-crm-last-error">
                Ultimo errore ({status.last_error_tab}): {status.last_error}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            <MiniStat label="Contatti" value={status.contacts_total} />
            <MiniStat label="Synced" value={outbox.synced} tone="emerald" testId="pv-admin-crm-outbox-synced" />
            <MiniStat label="In coda" value={outbox.pending} tone="amber" testId="pv-admin-crm-outbox-pending" />
            <MiniStat label="Falliti" value={outbox.failed} tone="red" testId="pv-admin-crm-outbox-failed" />
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button onClick={fetchStatus} className="pv-btn-ghost !py-2 !px-3 !text-[12px]" data-testid="pv-admin-crm-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={testConnection}
              disabled={testing}
              data-testid="pv-admin-sheet-test"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 hover:border-emerald-400 text-[12px] font-bold transition-colors disabled:opacity-50"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />}
              Test
            </button>
            <button
              onClick={retryOutbox}
              disabled={retrying || !ok || (outbox.pending + outbox.failed) === 0}
              data-testid="pv-admin-sheet-retry"
              title="Riprova a spingere gli elementi pending/failed della outbox"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 hover:border-emerald-400 text-[12px] font-bold transition-colors disabled:opacity-50"
            >
              {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              Riprova coda
            </button>
            <button
              onClick={pushAll}
              disabled={pushing}
              title="Spinge tutti i record sui 4 tab (o li accoda se il provider manca)"
              data-testid="pv-admin-crm-sync-all"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-bold transition-colors disabled:opacity-40"
            >
              {pushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
              Sincronizza tutto
            </button>
          </div>
        </div>
      </div>

      {/* CSV exports */}
      <div className="mt-4 pt-4 border-t border-slate-200/70 flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mr-1">
          Export CSV compatibile col foglio:
        </span>
        {EXPORT_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => exportTab(t.key)}
            disabled={exporting === t.key}
            data-testid={`pv-admin-sheet-export-${t.key}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-[11.5px] font-bold hover:border-emerald-400 hover:text-emerald-700 transition-colors disabled:opacity-50"
          >
            {exporting === t.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            {t.label}
          </button>
        ))}
      </div>

      {/* Apps Script setup instructions */}
      {!ok && (
        <div className="mt-4 pt-4 border-t border-slate-200/70" data-testid="pv-admin-webhook-form">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold mb-2">
            ⚡ Collega il webhook ora (senza riavvio, vale anche in produzione)
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={whUrl}
              onChange={(e) => setWhUrl(e.target.value)}
              placeholder="https://script.google.com/macros/s/…/exec"
              data-testid="pv-admin-webhook-url"
              className="flex-1 h-9 px-3 rounded-xl border border-slate-300 text-[12px] font-mono outline-none focus:border-emerald-400"
            />
            <input
              value={whToken}
              onChange={(e) => setWhToken(e.target.value)}
              placeholder="Token (opzionale se già in env)"
              data-testid="pv-admin-webhook-token"
              className="md:w-56 h-9 px-3 rounded-xl border border-slate-300 text-[12px] font-mono outline-none focus:border-emerald-400"
            />
            <button
              onClick={saveWebhook}
              disabled={savingWh}
              data-testid="pv-admin-webhook-save"
              className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-bold transition-colors disabled:opacity-50"
            >
              {savingWh ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />}
              Collega webhook
            </button>
          </div>
        </div>
      )}
      {!ok && (
        <details className="mt-4 pt-4 border-t border-slate-200/70" data-testid="pv-admin-sheet-instructions">
          <summary className="cursor-pointer font-bold text-[13px] text-amber-800 select-none">
            📋 Come attivare la sync senza JSON (webhook Apps Script — 5 minuti)
          </summary>
          <ol className="mt-3 space-y-2 text-[12.5px] text-slate-700 leading-relaxed list-decimal pl-5">
            <li>Apri il <a href={status.sheet_url} target="_blank" rel="noopener noreferrer" className="text-pv-teal font-bold underline">Google Sheet CRM</a> → menu <strong>Estensioni → Apps Script</strong>.</li>
            <li>Cancella il contenuto e incolla il codice qui sotto. Sostituisci <code className="font-mono bg-slate-100 px-1 rounded">IL_TUO_TOKEN</code> con un token segreto a tua scelta (lungo e casuale).</li>
            <li>In alto a destra: <strong>Implementa → Nuova implementazione → App web</strong>. Esegui come: <strong>me</strong> · Chi ha accesso: <strong>Chiunque</strong>. Autorizza e copia l&apos;URL che termina con <code className="font-mono bg-slate-100 px-1 rounded">/exec</code>.</li>
            <li>Nel backend, in <code className="font-mono bg-slate-100 px-1 rounded">backend/.env</code>, imposta:
              <div className="font-mono text-[11px] bg-slate-900 text-emerald-300 rounded-lg p-3 mt-1.5 overflow-x-auto">
                {status.webhook_url_env || 'GOOGLE_APPS_SCRIPT_WEBHOOK_URL'}=&lt;URL /exec copiato&gt;<br />
                {status.webhook_token_env || 'CRM_SHEETS_WEBHOOK_TOKEN'}=&lt;lo stesso token del codice&gt;
              </div>
            </li>
            <li>Riavvia il backend, poi premi <strong>Test</strong> qui sopra e infine <strong>Sincronizza tutto</strong>.</li>
          </ol>
          <div className="mt-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1.5">Codice Apps Script (doPost)</div>
            <pre className="font-mono text-[10.5px] bg-slate-900 text-slate-200 rounded-lg p-3 overflow-x-auto max-h-64 leading-relaxed">{APPS_SCRIPT_CODE}</pre>
            <button
              onClick={() => { navigator.clipboard.writeText(APPS_SCRIPT_CODE); toast.success('Codice copiato'); }}
              className="mt-2 pv-btn-ghost !py-1.5 !px-3 !text-[11.5px]"
              data-testid="pv-admin-sheet-copy-script"
            >
              Copia codice
            </button>
          </div>
        </details>
      )}
    </div>
  );
};

const MiniStat = ({ label, value, tone, testId }) => (
  <div className="text-center" data-testid={testId}>
    <div className={`font-heading font-extrabold text-[20px] leading-none ${
      tone === 'emerald' ? 'text-emerald-600' : tone === 'amber' ? 'text-amber-600' : tone === 'red' ? 'text-red-600' : 'text-pv-ink'
    }`}>
      {value ?? '—'}
    </div>
    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400 font-bold mt-1">{label}</div>
  </div>
);

export default CrmStatusBanner;
